import React from 'react'
import { Video } from 'lucide-react'
import ContentRenderer from '../../../components/ContentRenderer'
import { cardToContentItems } from '../lib/cardToContentItem'
import { genId } from '../lib/id'
import type { VideoCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'
import { InteractiveVideoPlayer, VideoQuestionEditor } from './InteractiveVideoQuestions'

function parseYouTubeId(input: string): string | null {
  if (!input) return null
  const m = input.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/)
  if (m) return m[1]
  if (/^[\w-]{11}$/.test(input.trim())) return input.trim()
  return null
}

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as VideoCard
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Link YouTube hoặc URL video</label>
        <input
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
          value={c.youtubeId || c.src || ''}
          onChange={(e) => {
            const val = e.target.value
            const yt = parseYouTubeId(val)
            if (yt) onChange({ youtubeId: yt, src: undefined })
            else onChange({ src: val, youtubeId: undefined })
          }}
          placeholder="https://youtu.be/... hoặc https://.../video.mp4"
        />
      </div>
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Chú thích (tuỳ chọn)</label>
        <input
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
          value={c.caption || ''}
          onChange={(e) => onChange({ caption: e.target.value })}
        />
      </div>
      <VideoQuestionEditor
        questionPool={c.questionPool || []}
        checkpoints={c.checkpoints || []}
        onPoolChange={(next) => onChange({ questionPool: next })}
        onChange={(next) => onChange({ checkpoints: next })}
      />
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card, onComplete }) => {
  const c = card as VideoCard
  if (c.youtubeId) {
    return (
      <div className="my-2">
        <InteractiveVideoPlayer
          source={{ kind: 'youtube', youtubeId: c.youtubeId }}
          title={c.title || 'YouTube video'}
          checkpoints={c.checkpoints || []}
          onComplete={onComplete}
        />
        {c.caption && <p className="text-xs sm:text-sm text-gray-600 mt-2 italic text-center">{c.caption}</p>}
      </div>
    )
  }
  if (c.src) {
    return (
      <div className="my-2">
        <InteractiveVideoPlayer
          source={{ kind: 'video', src: c.src, isAudio: /\.(mp3|wav|m4a)(\?|$)/i.test(c.src) }}
          title={c.title || 'Video'}
          checkpoints={c.checkpoints || []}
          onComplete={onComplete}
        />
        {c.caption && <p className="text-xs sm:text-sm text-gray-600 mt-2 italic text-center">{c.caption}</p>}
      </div>
    )
  }
  const items = cardToContentItems(card)
  return items ? <ContentRenderer content={items} /> : null
}

export const videoCardDef: CardDef = {
  type: 'video',
  label: 'Video',
  description: 'YouTube hoặc video tải lên, có thể chèn câu hỏi theo mốc thời gian',
  icon: Video,
  color: 'bg-rose-50 text-rose-600',
  createDefault: (): VideoCard => ({ id: genId(), type: 'video' }),
  Editor,
  Preview,
}
